import React, { useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Skeleton,
  Autocomplete,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import 'date-fns';
import { AlertMessagesContext } from 'react-alert-messages';
import styles from './styles.module.css';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import PurchaseService from 'services/PurchaseService';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import ItemService from 'services/ItemService';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { ROUTES, SUBSCRIPTION_TYPE } from 'const';
import EditIcon from '@mui/icons-material/Edit';
import VendorServices from 'services/VendorServices';
import AccountServices from 'services/AccountServices';
import { VendorContext } from 'Context/VendorContext';
// import Loader from 'components/utils/Loading';
import CustomMenuItem from 'components/utils/CustomComponents/CustomMenuItem';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import NewVendorDialog from 'components/popups/NewVendorDialog';
import { featureIsExpired } from 'components/utils/FeatureValidityExpire';
import { ShopContext } from 'Context/ShopContext';
import { InventoryContext } from 'Context/InventoryContext';
import RefundTypePopup from 'components/popups/RefundTypePopup';
import ShopsHelper from 'helpers/ShopsHelper';
import NumberField from 'components/library/NumberField';
import BarcodeHelper from 'helpers/BarcodeHelper';
import useBarcodeScanner from 'hooks/useBarcodeScanner';
import { useTranslation } from 'react-i18next';
import BatchSelectionPopup from 'components/popups/BatchSelectionPopup';
import InputAdornment from '@mui/material/InputAdornment';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { WarehouseContext } from 'Context/WarehouseContext';

function AddPurchaseReturn() {
  const { t: tCommon } = useTranslation('common');
  const { t: tOperations } = useTranslation('operations');
  const { shop } = useContext(ShopContext);
  const { products: items, getProducts, stockBatches, getStockBatches } = useContext(InventoryContext);
  const { warehouses, getWarehouses, warehouseProducts, getWarehouseProducts } = useContext(WarehouseContext);

  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const purchaseId = queryParams.get('purchase_id');

  const { postAlertMessage } = useContext(AlertMessagesContext);
  const [editData, setEditData] = useState({});
  const [vendorId, setVendorId] = useState(null);
  const [vendorNameOld, setVendorNameOld] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [returnDate, setReturnDate] = useState(new Date());
  const [billDate, setBillDate] = useState(new Date());
  const [item, setItem] = useState(null);
  const [itemPlu, setItemPlu] = useState('');
  const [itemName, setItemName] = useState('');
  const [cost, setCost] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [tax, setTax] = useState(0);
  const [taxPercentage, setTaxPercentage] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [qtyError, setQtyError] = useState('');
  const [returnedItems, setReturnedItems] = useState([]);
  const [removedItems, setRemovedItems] = useState([]);
  const { vendors, getVendors, setVendors } = useContext(VendorContext);
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [isShowAddVendonDialog, setShowAddVendonDialog] = useState(false);
  const [stock, setStock] = useState(0);
  const [showReturnTypePopup, setShowReturnTypePopup] = useState(false);
  const [paymentModes, setPaymentModes] = useState(null);
  const [selectedItemBatches, setSelectedItemBatches] = useState([]);
  const [batchPopupAnchor, setBatchPopupAnchor] = useState(null);
  const [localBatches, setLocalBatches] = useState([]);
  const [originalBatchQtys, setOriginalBatchQtys] = useState({});
  const [warehouseId, setWarehouseId] = useState(null);

  const featureValidity = shop?.customer_support_validity;

  const getPaymentModes = async () => {
    const resp = await AccountServices.getAccountHeads();
    setPaymentModes(
      (resp || []).map((mode) => ({ mode_id: mode.id, name: mode.name, amount: 0 }))
    );
  };

  const onCreateVendor = async () => {
    const updatedVendors = await VendorServices.getVendors();
    setVendors(updatedVendors || []);
    setShowAddVendonDialog(false);
  };

  const openCreateVendorDialog = () => {
    setShowAddVendonDialog(true);
  };
  const closeCreateVendorDialog = () => {
    setShowAddVendonDialog(false);
  };

  const handleChangePayable = (val) => {
    if (quantity === 0 || item === null) return;

    const rawCost = (val - tax) / quantity;
    setTotalAmount(ShopsHelper.getAmountFormatted(shop, val));
    setCost(ShopsHelper.getAmountFormatted(shop, rawCost));
  };

  const handleChangeRate = (val) => {
    if (quantity === 0 || item === null) return;

    const rawTotal = val * quantity + tax;

    setCost(ShopsHelper.getAmountFormatted(shop, val));
    setTotalAmount(ShopsHelper.getAmountFormatted(shop, rawTotal));
  };

  const handleChangeQty = (val) => {
    if (item === null) return;
    let numVal = Number(val);
    if (numVal < 0) numVal = 0;
    setQuantity(String(numVal));
    const currentWhId = warehouseId === 0 ? null : warehouseId;
    let initialStock = item?.stock || 0;
    if (currentWhId && Array.isArray(warehouseProducts)) {
      const wp = warehouseProducts.find((p) => {
        const bWhId = p.warehouse?.id || p.warehouse || null;
        const bItemId = p.item?.id || p.item || null;
        return String(bWhId) === String(currentWhId) && String(bItemId) === String(item.id);
      });
      initialStock = wp ? wp.stock : 0;
    }
    setStock(initialStock - numVal);
    setTotalAmount(ShopsHelper.getAmountFormatted(shop, numVal * Number(cost) + Number(tax)));
    if (numVal > 0) {
      setQtyError('');
    }
  };

  const handleChangeTaxAmount = (val) => {
    if (item === null) return;
    const amount = Number(val) || 0;
    const base = cost * quantity;

    const taxPercentage = (amount / base) * 100;

    setTax(amount);
    setTaxPercentage(taxPercentage);
    setTotalAmount(ShopsHelper.getAmountFormatted(shop, base + amount));
  };

  const handleChangeTaxPercentage = (val) => {
    if (item === null) return;
    const percent = Number(val) || 0;
    const base = cost * quantity;

    const tax = (percent * base) / 100;

    setTaxPercentage(percent);
    setTax(tax);
    setTotalAmount(ShopsHelper.getAmountFormatted(shop, base + tax));
  };

  const handleBatchQuantityChange = (batchId, val) => {
    let qty = Number(val);
    if (qty < 0) qty = 0;

    const updateBatchList = (prevBatches, bId, newQty) => {
      const existing = prevBatches.find((b) => b.batch_id === bId);
      if (existing) {
        if (newQty <= 0) {
          return prevBatches.filter((b) => b.batch_id !== bId);
        } else {
          return prevBatches.map((b) => (b.batch_id === bId ? { ...b, quantity: newQty } : b));
        }
      } else {
        return [...prevBatches, { batch_id: bId, quantity: newQty }];
      }
    };

    setSelectedItemBatches((prev) => {
      const newBatches = updateBatchList(prev, batchId, qty);
      const totalBatchQty = newBatches.reduce((sum, b) => sum + b.quantity, 0);
      handleChangeQty(totalBatchQty);
      return newBatches;
    });
  };

  const pluInputRef = useRef(null);
  const nameInputRef = useRef(null);
  const costInputRef = useRef(null);
  const qtyInputRef = useRef(null);

  const filteredItemsList = useMemo(() => {
    return items || [];
  }, [items]);

  const selectItemHandler = useCallback(
    (newItem) => {
      if (!newItem) {
        setItem(null);
        setItemName('');
        setItemPlu('');
        setCost('');
        setStock(0);
        setQuantity(1);
        setTotalAmount(0);
        setSelectedItemBatches([]);
        setQtyError('');
        return;
      }
      setItem(newItem);
      setItemName(newItem?.name || '');
      setItemPlu(newItem?.plu ? String(newItem.plu) : '');
      setCost(newItem?.cost || '');
      const currentWhId = warehouseId === 0 ? null : warehouseId;
      let initialStock = newItem?.stock || 0;
      if (currentWhId && Array.isArray(warehouseProducts)) {
        const wp = warehouseProducts.find((p) => {
          const bWhId = p.warehouse?.id || p.warehouse || null;
          const bItemId = p.item?.id || p.item || null;
          return String(bWhId) === String(currentWhId) && String(bItemId) === String(newItem.id);
        });
        initialStock = wp ? wp.stock : 0;
      }
      setStock(initialStock);
      const defaultQty = newItem?.is_batch_enabled ? 0 : 1;
      setQuantity(defaultQty);
      setTotalAmount((newItem?.cost || 0) * defaultQty);
      setSelectedItemBatches([]);
      setQtyError('');
    },
    [warehouseId, warehouseProducts]
  );



  const handleItemSearchKeyDown = (e, inputValue, isPluField = false) => {
    if (e.key === 'Enter') {
      const trimmedInput = (inputValue || '').trim();
      const isCurrentItemMatch = BarcodeHelper.isItemMatch(item, trimmedInput);

      if (item && isCurrentItemMatch && quantity && Number(quantity) > 0) {
        addItem();
        return;
      }

      if (trimmedInput) {
        const matched = BarcodeHelper.findMatchingItem(trimmedInput, filteredItemsList);
        if (matched) {
          e.preventDefault();
          selectItemHandler(matched);
          if (qtyInputRef.current) qtyInputRef.current.focus();
          return;
        }
      }
    }
  };

  useBarcodeScanner({
    onScan: (scannedCode) => {
      const matched = BarcodeHelper.findMatchingItem(scannedCode, filteredItemsList);
      if (matched) {
        selectItemHandler(matched);
        setQuantity(1);
        if (qtyInputRef.current) qtyInputRef.current.focus();
      } else {
        setQuantity(1);
        postAlertMessage({
          text: `No item found matching barcode/PLU: "${scannedCode}"`,
          type: 'warning',
        });
      }
    },
    isCapture: true,
    deps: [filteredItemsList, selectItemHandler, postAlertMessage],
  });

  const resetItemFields = () => {
    setItemName('');
    setItemPlu('');
    setCost('');
    setQuantity(1);
    setTax('');
    setTotalAmount('');
    setItem(null);
    setStock('');
    setTaxPercentage('');
    setSelectedItemBatches([]);
    setBatchPopupAnchor(null);
    setQtyError('');
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  };

  const addItem = () => {
    if (!quantity || Number(quantity) <= 0) {
      setQtyError(tOperations('Quantity must be greater than 0'));
      postAlertMessage({
        text: tOperations('Quantity must be greater than 0'),
        type: 'failed',
      });
      return;
    }
    let data = {
      plu: item.plu,
      item: item,
      rate: Number(cost),
      quantity: Number(quantity),
      tax: Number(tax),
      payable: Number(totalAmount),
      stock: Number(stock),
      batch_data: selectedItemBatches,
    };
    if (editData && !(editData?.items || []).find((itm) => itm.item === item && itm.rate === cost)) {
      data = {
        ...data,
        purchase: editData.id,
        new_item: true,
      };
    } else {
      const updatingItem = (editData?.items || []).find((itm) => itm.item === item && itm.rate === cost);
      data = {
        ...updatingItem,
        ...data,
      };
    }
    setReturnedItems([...returnedItems, data]);
    resetItemFields();
  };

  const handleAddPurchaseReturn = async (refundType, paymentModeId) => {
    setLoadingIndicator(true);
    try {
      const data = {
        vendor_id: vendorId,
        invoice_no: invoiceNumber,
        bill_date: DateTimeHelpers.convertDateToDMY(billDate),
        return_date: DateTimeHelpers.convertDateToDMY(returnDate),
        items: returnedItems.map((pi) => ({ ...pi, item_id: pi.item.id })),
        refund_type: refundType,
        payment_mode_id: paymentModeId,
        payment_modes: paymentModes || [],
        purchase_id: purchaseId,
        warehouse_id: warehouseId === 0 ? null : warehouseId,
      };
      await PurchaseService.addPurchaseReturn(data);

      const isPremium = shop?.subscription?.type === SUBSCRIPTION_TYPE.PREMIUM;
      const hasBatchEnabled = returnedItems.some((pi) => pi.item?.is_batch_enabled);
      const isWarehouseEnabled = !featureIsExpired(shop?.warehouse_validity);

      const promises = [getProducts({ useCache: false })];
      if (isPremium && hasBatchEnabled) {
        promises.push(getStockBatches({ useCache: false }));
      }
      if (isWarehouseEnabled) {
        promises.push(getWarehouseProducts({ useCache: false }));
      }
      await Promise.all(promises);
      postAlertMessage({
        text: tOperations('Purchase return added successfully'),
        type: 'success',
      });
      history.push(ROUTES.PURCHASE_RETURN_HISTORY);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const dltItem = (item) => {
    if (item.hasOwnProperty('id')) {
      setRemovedItems((old) => [...old, item.id]);
    }
    setReturnedItems(
      returnedItems.filter((itm) => itm.rate !== item.rate || itm.plu !== item.plu || itm.tax !== item.tax)
    );
  };

  const handleEditItem = async (returnItem) => {
    setLoadingIndicator(true);
    try {
      setItem(returnItem.item);
      setItemName(returnItem.item?.name);
      setItemPlu(returnItem?.plu);
      setQuantity(returnItem?.quantity);
      setCost(returnItem?.rate);
      setTax(returnItem?.tax);
      setTotalAmount(returnItem?.rate * returnItem.quantity + returnItem?.tax);
      setReturnedItems(
        returnedItems.filter(
          (itm) => itm.rate !== returnItem.rate || itm.plu !== returnItem.plu || itm.tax !== returnItem.tax
        )
      );
      setStock(items.find((itm) => itm.id === returnItem?.item.id)?.stock - Number(returnItem?.quantity));
      setQtyError('');
      const batches = returnItem.config?.batch_data || returnItem.batch_data || [];
      setSelectedItemBatches(batches);

      // Fetch any missing batch metadata if needed
      const missingBatchIds = batches
        .map((b) => b.batch_id)
        .filter((id) => !localBatches.some((lb) => String(lb.id) === String(id)));

      if (missingBatchIds.length > 0) {
        const fetched = await Promise.all(missingBatchIds.map((id) => ItemService.getStockBatch(id)));
        setLocalBatches((prev) => {
          const existingIds = prev.map((b) => String(b.id));
          const uniqueNew = fetched.filter((b) => b && !existingIds.includes(String(b.id)));
          return [...prev, ...uniqueNew];
        });
      }
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const handleEditPurchaseReturn = async () => {
    setLoadingIndicator(true);
    try {
      const _items = returnedItems.map((pi) => ({
        ...pi,
        item_id: pi.item.id,
      }));
      const olditems = _items.filter((returnItem) => !returnItem.new_item);
      const newItems = _items.filter((returnItem) => returnItem.new_item);
      const data = {
        ...editData,
        vendor_id: vendorId,
        bill_date: DateTimeHelpers.convertDateToDMY(billDate),
        return_date: DateTimeHelpers.convertDateToDMY(returnDate),
        items: olditems,
        new_items: newItems,
        remove_items: removedItems,
        warehouse_id: warehouseId === 0 ? null : warehouseId,
        payment_modes: (paymentModes || []).map((pm) => ({
          mode_id: pm.mode_id,
          name: pm.name,
          amount: Number(pm.amount || 0),
        })),
      };
      await PurchaseService.updatePurchaseReturn(data, id);

      const isPremium = shop?.subscription?.type === SUBSCRIPTION_TYPE.PREMIUM;
      const hasBatchInReturned = returnedItems.some((pi) => pi.item?.is_batch_enabled);
      const hasBatchInRemoved =
        Array.isArray(removedItems) &&
        removedItems.some((removedId) => {
          const origItem = (editData?.items || []).find((pi) => pi.id === removedId);
          return origItem?.item?.is_batch_enabled;
        });
      const hasBatchEnabled = hasBatchInReturned || hasBatchInRemoved;
      const isWarehouseEnabled = !featureIsExpired(shop?.warehouse_validity);

      const promises = [getProducts({ useCache: false })];
      if (isPremium && hasBatchEnabled) {
        promises.push(getStockBatches({ useCache: false }));
      }
      if (isWarehouseEnabled) {
        promises.push(getWarehouseProducts({ useCache: false }));
      }
      await Promise.all(promises);
      postAlertMessage({
        text: tOperations('Purchase return updated successfully'),
        type: 'success',
      });
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
    setLoadingIndicator(false);
    history.push(ROUTES.PURCHASE_RETURN_HISTORY);
  };

  const getPurchaseReturnData = async (loadedItems = [], loadedBatches = []) => {
    try {
      const purchaseReturn = await PurchaseService.getPurchaseReturn(id);
      setEditData(purchaseReturn);
      setReturnDate(new Date(purchaseReturn.return_date));
      setBillDate(new Date(purchaseReturn.bill_date));
      setReturnedItems(purchaseReturn.items);
      setVendorId(purchaseReturn.vendor?.id);
      setInvoiceNumber(purchaseReturn.invoice_no);
      setVendorNameOld(purchaseReturn.vendorNameOld);

      const processedItems = (purchaseReturn.items || []).map((pi) => {
        let updatedItem = pi?.item;
        if (updatedItem?.id) {
          const invItem = loadedItems.find((p) => p.id === updatedItem.id);
          if (invItem) {
            updatedItem = invItem;
          }
        }
        return {
          ...pi,
          item: updatedItem,
          batch_data: pi.config?.batch_data || pi.batch_data || [],
        };
      });
      setReturnedItems(processedItems);

      const missingBatchIds = [];
      const origQtys = {};
      processedItems.forEach((item) => {
        (item.batch_data || []).forEach((sb) => {
          origQtys[String(sb.batch_id)] = (origQtys[String(sb.batch_id)] || 0) + (Number(sb.quantity) || 0);
          if (!loadedBatches.some((lb) => String(lb.id) === String(sb.batch_id))) {
            missingBatchIds.push(sb.batch_id);
          }
        });
      });
      setOriginalBatchQtys(origQtys);

      if (missingBatchIds.length > 0) {
        const fetched = await Promise.all(missingBatchIds.map((id) => ItemService.getStockBatch(id)));
        setLocalBatches((prev) => {
          const existingIds = prev.map((b) => String(b.id));
          const uniqueNew = fetched.filter((b) => b && !existingIds.includes(String(b.id)));
          return [...prev, ...uniqueNew];
        });
      }
    } catch (err) {
      console.error('Failed to load purchase return data:', err);
      postAlertMessage({ text: err.message, type: 'failed' });
    }
  };

  const getPurchaseData = async (loadedItems = [], loadedBatches = []) => {
    try {
      const purchase = await PurchaseService.getPurchase(purchaseId);
      setEditData({ ...purchase, id: null });
      setReturnDate(new Date());
      setReturnedItems(purchase.items);
      setVendorId(purchase.vendor?.id);
      setInvoiceNumber(purchase.invoice_no);
      setVendorNameOld(purchase.vendorNameOld);

      if (purchase.warehouse) {
        setWarehouseId(purchase.warehouse.id || purchase.warehouse);
      } else if (shop?.config?.default_warehouse) {
        setWarehouseId(shop.config.default_warehouse);
      }

      const processedItems = (purchase.items || []).map((pi) => {
        let updatedItem = pi?.item;
        if (updatedItem?.id) {
          const invItem = loadedItems.find((p) => p.id === updatedItem.id);
          if (invItem) {
            updatedItem = invItem;
          }
        }
        return {
          ...pi,
          item: updatedItem,
          batch_data: pi.config?.batch_data || pi.batch_data || [],
        };
      });
      setReturnedItems(processedItems);

      const missingBatchIds = [];
      const origQtys = {};
      processedItems.forEach((item) => {
        (item.batch_data || []).forEach((sb) => {
          origQtys[String(sb.batch_id)] = (origQtys[String(sb.batch_id)] || 0) + (Number(sb.quantity) || 0);
          if (!loadedBatches.some((lb) => String(lb.id) === String(sb.batch_id))) {
            missingBatchIds.push(sb.batch_id);
          }
        });
      });
      setOriginalBatchQtys(origQtys);

      if (missingBatchIds.length > 0) {
        const fetched = await Promise.all(missingBatchIds.map((id) => ItemService.getStockBatch(id)));
        setLocalBatches((prev) => {
          const existingIds = prev.map((b) => String(b.id));
          const uniqueNew = fetched.filter((b) => b && !existingIds.includes(String(b.id)));
          return [...prev, ...uniqueNew];
        });
      }
    } catch (err) {
      console.error('Failed to load purchase data:', err);
      postAlertMessage({ text: err.message, type: 'failed' });
    }
  };

  const fetchInitialData = async () => {
    setLoadingIndicator(true);
    try {
      const [, loadedProducts, loadedBatches] = await Promise.all([
        getVendors(),
        getProducts(),
        getStockBatches(),
        getPaymentModes(),
        getWarehouses({ useCache: true }),
        getWarehouseProducts({ useCache: true }),
      ]);

      if (loadedBatches) {
        setLocalBatches((prev) => {
          const existingIds = prev.map((b) => String(b.id));
          const uniqueNew = (loadedBatches || []).filter((b) => !existingIds.includes(String(b.id)));
          return [...prev, ...uniqueNew];
        });
      }

      if (id) {
        await getPurchaseReturnData(loadedProducts || [], loadedBatches || []);
      } else if (purchaseId) {
        await getPurchaseData(loadedProducts || [], loadedBatches || []);
      }
    } catch (err) {
      console.error('Failed to load initial data:', err);
    } finally {
      setLoadingIndicator(false);
    }
  };

  const handleShowReturnTypePopup = () => {
    const hasInvalidQty = returnedItems.some((itm) => !itm.quantity || Number(itm.quantity) <= 0);
    if (hasInvalidQty) {
      postAlertMessage({
        text: tOperations('All items in purchase return must have a quantity greater than 0'),
        type: 'failed',
      });
      return;
    }

    if (editData.id) {
      handleEditPurchaseReturn();
    } else if (editData.paid_amount && editData.paid_amount !== 0) {
      setShowReturnTypePopup(true);
    } else {
      const hasPayment = (paymentModes || []).some((pm) => Number(pm.amount) > 0);
      handleAddPurchaseReturn(hasPayment ? 'refund' : 'credit');
    }
  };

  const handleClosePurchseReturnTypePopup = () => {
    setShowReturnTypePopup(false);
  };

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, purchaseId]);

  useEffect(() => {
    if (editData && editData.config && editData.config.payment_modes && paymentModes && paymentModes.length > 0) {
      const hasAnyAmount = paymentModes.some((pm) => Number(pm.amount || 0) > 0);
      if (!hasAnyAmount) {
        const oldModes = editData.config.payment_modes;
        const updatedPaymentModes = paymentModes.map((currentMode) => {
          const match = oldModes.find(
            (oldMode) => String(oldMode.mode_id || oldMode.id) === String(currentMode.mode_id || currentMode.id)
          );
          return match ? { ...currentMode, amount: match.amount } : currentMode;
        });
        const hasChanges = updatedPaymentModes.some((pm, idx) => pm.amount !== paymentModes[idx].amount);
        if (hasChanges) {
          setPaymentModes(updatedPaymentModes);
        }
      }
    }
  }, [editData, paymentModes]);

  useEffect(() => {
    if (!featureIsExpired(shop?.warehouse_validity) && shop?.config?.is_warehouse_enabled) {
      if (warehouses?.length > 0 && !id && !purchaseId && warehouseId === null) {
        if (shop?.config?.default_warehouse) {
          setWarehouseId(shop.config.default_warehouse);
        }
      }
    }
    // eslint-disable-next-line
  }, [warehouses, shop, id, purchaseId]);

  useEffect(() => {
    if (stockBatches && stockBatches.length > 0) {
      setLocalBatches((prev) => {
        const stockBatchesMap = new Map(stockBatches.map((b) => [String(b.id), b]));
        const updatedPrev = prev.map((b) => {
          const key = String(b.id);
          if (stockBatchesMap.has(key)) {
            return stockBatchesMap.get(key);
          }
          return b;
        });
        const prevIds = new Set(prev.map((b) => String(b.id)));
        const newBatches = stockBatches.filter((b) => !prevIds.has(String(b.id)));
        return [...updatedPrev, ...newBatches];
      });
    }
  }, [stockBatches]);

  useEffect(() => {
    if (item) {
      const currentWhId = warehouseId === 0 ? null : warehouseId;
      let s = item.stock || 0;
      if (currentWhId) {
        const wp = warehouseProducts.find((p) => {
          const bWhId = p.warehouse?.id || p.warehouse || null;
          const bItemId = p.item?.id || p.item || null;
          return String(bWhId) === String(currentWhId) && String(bItemId) === String(item.id);
        });
        s = wp ? wp.stock : 0;
      }
      setStock(s);
    }
  }, [warehouseId, item, warehouseProducts, quantity]);

  const _getPurchaseReturnSummary = (items) => {
    let totalPayable = 0;
    let totalTax = 0;
    let totalQty = 0;
    items?.forEach((item) => {
      totalPayable += item.rate * item.quantity + item.tax;
      totalTax += item.tax;
      totalQty += item.quantity;
    });

    return {
      totalPayable: ShopsHelper.getAmountFormatted(shop, totalPayable),
      totalTax: ShopsHelper.getAmountFormatted(shop, totalTax),
      totalQty: totalQty,
    };
  };

  const summary = _getPurchaseReturnSummary(returnedItems);

  const filteredStockBatches = useMemo(() => {
    const currentWhId = warehouseId === 0 ? null : warehouseId;
    if (!currentWhId || !Array.isArray(localBatches)) return localBatches || [];
    return localBatches.filter((b) => {
      const bWhId = b.warehouse?.id || b.warehouse || null;
      return String(bWhId) === String(currentWhId);
    });
  }, [warehouseId, localBatches]);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <h2 className={styles.title}>
          {tOperations('Purchase Return')} <span className={styles.menuTitle}>{tCommon('Management')}</span>
        </h2>
      </div>
      <div className={styles.changeable}>
        <div className={styles.filterSec}>
          <div className={styles.headTitle}>
            <h2 className={styles.subTitle}>{tOperations('New Purchase Return')}</h2>
          </div>
        </div>
        {loadingIndicator ? (
          <Grid container alignItems="center" spacing={2} p={2}>
            <Grid item xs={6}>
              <Skeleton variant="rectangular" height={40} animation="wave" />
            </Grid>
            <Grid item xs={6}>
              <Skeleton variant="rectangular" height={40} animation="wave" />
            </Grid>
            <Grid item xs={6}>
              <Skeleton variant="rectangular" height={40} animation="wave" />
            </Grid>
            <Grid item xs={6}>
              <Skeleton variant="rectangular" height={40} animation="wave" />
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={3}>
            {/* Column 1: Vendor, Vendor Balance */}
            <Grid item xs={12} md={4}>
              <Grid container direction="column" spacing={2}>
                <Grid item>
                  {shop && featureIsExpired(featureValidity) ? (
                    <TextField
                      fullWidth
                      label={tOperations('Vendor')}
                      value={vendorNameOld || ''}
                      variant="outlined"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => {
                        setVendorNameOld(e.target.value);
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#fff',
                          borderRadius: '8px',
                          '& fieldset': { borderColor: '#e2e8f0' },
                          '&:hover fieldset': { borderColor: '#cbd5e0' },
                          '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                        },
                        '& input': {
                          paddingTop: '10.5px',
                          paddingBottom: '10.5px',
                          fontSize: '14px',
                        },
                      }}
                    />
                  ) : (
                    <TextField
                      select
                      fullWidth
                      label={tOperations('Vendor')}
                      value={vendorId || 0}
                      variant="outlined"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#fff',
                          borderRadius: '8px',
                          '& fieldset': { borderColor: '#e2e8f0' },
                          '&:hover fieldset': { borderColor: '#cbd5e0' },
                          '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                        },
                        '& .MuiSelect-select': {
                          paddingTop: '10.5px',
                          paddingBottom: '10.5px',
                          fontSize: '14px',
                        },
                      }}
                      onChange={(e) => {
                        setVendorId(Number(e.target.value));
                      }}
                    >
                      <MenuItem value={0}>
                        <em>{tOperations('No Vendor Selected')}</em>
                      </MenuItem>
                      {(vendors || []).map((v) => (
                        <MenuItem key={v.id} value={v.id}>
                          {v.name}
                        </MenuItem>
                      ))}
                      <CustomMenuItem onClick={openCreateVendorDialog}>
                        <AddCircleIcon fontSize="small" style={{ marginLeft: 5 }} />
                        <p className={styles.normalText} style={{ fontSize: '12px' }}>
                          {tOperations('Create New Vendor')}
                        </p>
                      </CustomMenuItem>
                    </TextField>
                  )}
                </Grid>
                <Grid item>
                  {(() => {
                    const selectedVendor = vendors?.find((v) => v.id === vendorId);
                    return (
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        label={tOperations('Vendor Balance')}
                        InputProps={{ readOnly: true }}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#f8fafc',
                            borderRadius: '8px',
                            '& fieldset': { borderColor: '#e2e8f0' },
                          },
                          '& input': {
                            paddingTop: '10.5px',
                            paddingBottom: '10.5px',
                            fontSize: '14px',
                          },
                        }}
                        value={selectedVendor ? ShopsHelper.getAmountFormatted(shop, selectedVendor.balance) : '—'}
                      />
                    );
                  })()}
                </Grid>
              </Grid>
            </Grid>

            {/* Column 2: All Dates */}
            <Grid item xs={12} md={4}>
              <Grid container direction="column" spacing={2}>
                <Grid item>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      format="dd/MM/yyyy"
                      value={billDate}
                      label={tCommon('Bill Date')}
                      onChange={(date) => {
                        setBillDate(date);
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'outlined',
                          fullWidth: true,
                          InputLabelProps: { shrink: true },
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#fff',
                              borderRadius: '8px',
                              '& fieldset': { borderColor: '#e2e8f0' },
                              '&:hover fieldset': { borderColor: '#cbd5e0' },
                              '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                            },
                            '& input': {
                              paddingTop: '10.5px',
                              paddingBottom: '10.5px',
                              fontSize: '14px',
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      format="dd/MM/yyyy"
                      value={returnDate}
                      label={tOperations('Return Date')}
                      onChange={(date) => {
                        setReturnDate(date);
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'outlined',
                          fullWidth: true,
                          InputLabelProps: { shrink: true },
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#fff',
                              borderRadius: '8px',
                              '& fieldset': { borderColor: '#e2e8f0' },
                              '&:hover fieldset': { borderColor: '#cbd5e0' },
                              '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                            },
                            '& input': {
                              paddingTop: '10.5px',
                              paddingBottom: '10.5px',
                              fontSize: '14px',
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
            </Grid>

            {/* Column 3: Invoice Number & Warehouse */}
            <Grid item xs={12} md={4}>
              <Grid container direction="column" spacing={2}>
                <Grid item>
                  <TextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    label={tCommon('Invoice Number')}
                    value={invoiceNumber}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        '& fieldset': { borderColor: '#e2e8f0' },
                        '&:hover fieldset': { borderColor: '#cbd5e0' },
                        '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                      },
                      '& input': {
                        paddingTop: '10.5px',
                        paddingBottom: '10.5px',
                        fontSize: '14px',
                      },
                    }}
                    onChange={(e) => {
                      setInvoiceNumber(e.target.value);
                    }}
                  />
                </Grid>
                {!featureIsExpired(shop?.warehouse_validity) && shop?.config?.is_warehouse_enabled && (
                  <Grid item>
                    <TextField
                      select
                      fullWidth
                      id="warehouse-select"
                      value={warehouseId || 0}
                      label={tOperations('Warehouse')}
                      variant="outlined"
                      size="small"
                      disabled={Boolean(id) || Boolean(purchaseId)}
                      onChange={(e) => {
                        const newWarehouseId = Number(e.target.value) === 0 ? null : Number(e.target.value);
                        if (newWarehouseId !== warehouseId) {
                          setWarehouseId(newWarehouseId);
                          setReturnedItems([]);
                          resetItemFields();
                          setOriginalBatchQtys({});
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#fff',
                          borderRadius: '8px',
                          '& fieldset': { borderColor: '#e2e8f0' },
                          '&:hover fieldset': { borderColor: '#cbd5e0' },
                          '&.Mui-focused fieldset': { borderColor: '#3182ce', borderWidth: '2px' },
                        },
                        '& .MuiSelect-select': {
                          paddingTop: '10.5px',
                          paddingBottom: '10.5px',
                          fontSize: '14px',
                        },
                      }}
                    >
                      <MenuItem value={0}>
                        <em>{tCommon('None')}</em>
                      </MenuItem>
                      {warehouses.map((warehouse) => (
                        <MenuItem key={warehouse.id} value={warehouse.id}>
                          {warehouse.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
      </div>
      <div className={styles.changeable} style={{ boxShadow: 'none', padding: '0', overflowX: 'auto' }}>
        <Table>
          <TableBody>
            <TableRow key="input row">
              <TableCell>
                <Autocomplete
                  value={item}
                  onChange={(event, newItem) => {
                    selectItemHandler(newItem);
                  }}
                  inputValue={itemPlu}
                  onInputChange={(e, value) => {
                    setItemPlu(value);
                  }}
                  getOptionLabel={(option) => (typeof option === 'object' && option !== null ? String(option.plu) : '')}
                  filterOptions={(options, { inputValue }) => {
                    const search = (inputValue || '').trim().toLowerCase();
                    if (!search) return options;

                    const startsWithSearch = [];
                    const containsSearch = [];

                    (options || []).forEach((option) => {
                      if (!option) return;
                      const pluStr = option.plu !== null && option.plu !== undefined ? String(option.plu).toLowerCase() : '';
                      const eanStr = option.ean && option.ean !== 'nil' ? String(option.ean).toLowerCase() : '';
                      const nameStr = option.name ? option.name.toLowerCase() : '';

                      if (pluStr.startsWith(search)) {
                        startsWithSearch.push(option);
                      } else if (pluStr.includes(search) || eanStr.includes(search) || nameStr.includes(search)) {
                        containsSearch.push(option);
                      }
                    });

                    return [...startsWithSearch, ...containsSearch];
                  }}
                  options={filteredItemsList}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={tCommon('PLU')}
                      variant="outlined"
                      size="small"
                      fullWidth
                      inputRef={pluInputRef}
                      onKeyDown={(e) => handleItemSearchKeyDown(e, itemPlu, true)}
                    />
                  )}
                />
              </TableCell>
              <TableCell>
                <Autocomplete
                  value={item}
                  onChange={(event, newItem) => {
                    selectItemHandler(newItem);
                  }}
                  inputValue={itemName}
                  onInputChange={(event, newInputValue) => {
                    setItemName(newInputValue);
                  }}
                  options={filteredItemsList}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option.name || '')}
                  filterOptions={(options, { inputValue }) => {
                    const cleanInput = (inputValue || '').trim().toLowerCase();
                    return (options || []).filter(
                      (option) =>
                        (option.name && option.name.toLowerCase().includes(cleanInput)) ||
                        (option.ean && option.ean !== 'nil' && option.ean.toLowerCase().includes(cleanInput)) ||
                        (option.plu !== undefined && option.plu !== null && String(option.plu).includes(cleanInput))
                    );
                  }}
                  renderOption={(props, option) => {
                    const eanText = option.ean && option.ean !== 'nil' ? option.ean : null;
                    return (
                      <li {...props} key={option.id || option.plu}>
                        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 500, fontSize: '14px' }}>{option.name}</span>
                            <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '8px' }}>
                              PLU: {option.plu}
                            </span>
                          </div>
                          {eanText && (
                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                              Barcode: {eanText}
                            </span>
                          )}
                        </div>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={tOperations('Search by Name/Barcode')}
                      variant="outlined"
                      size="small"
                      fullWidth
                      inputRef={nameInputRef}
                      onKeyDown={(e) => handleItemSearchKeyDown(e, itemName, false)}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            <Tooltip title="Barcode Scan Supported">
                              <QrCodeScannerIcon style={{ fontSize: '18px', color: '#2563eb', marginRight: 4 }} />
                            </Tooltip>
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  style={{ minWidth: '240px' }}
                />
              </TableCell>
              <TableCell>
                <NumberField
                  label={tCommon('Cost')}
                  value={ShopsHelper.getAmountFormatted(shop, cost)}
                  onChange={(e) => handleChangeRate(e.target.value)}
                  style={{ minWidth: '95px' }}
                  inputRef={costInputRef}
                />
              </TableCell>
              <TableCell>
                {item?.is_batch_enabled ? (
                  <NumberField
                    type="text"
                    label={tCommon('Quantity')}
                    value={quantity}
                    style={{ minWidth: '100px', cursor: 'pointer' }}
                    sx={{ '& input': { cursor: 'pointer' } }}
                    inputProps={{ readOnly: true }}
                    onKeyDown={(e) => e.preventDefault()}
                    InputProps={{
                      onClick: (e) => {
                        setBatchPopupAnchor(e.currentTarget);
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton color="primary" size="small">
                            <ArrowDropDownIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    error={Boolean(qtyError)}
                  />
                ) : (
                  <NumberField
                    label={tCommon('Quantity')}
                    value={quantity}
                    onChange={(e) => handleChangeQty(e.target.value)}
                    style={{ minWidth: '70px' }}
                    error={Boolean(qtyError)}
                  />
                )}
                <BatchSelectionPopup
                  open={Boolean(batchPopupAnchor)}
                  anchorEl={batchPopupAnchor}
                  onClose={() => {
                    setBatchPopupAnchor(null);
                  }}
                  item={item}
                  stockBatches={filteredStockBatches}
                  selectedBatches={selectedItemBatches}
                  originalBatchQtys={originalBatchQtys}
                  onChange={handleBatchQuantityChange}
                  showExpired={true}
                />
              </TableCell>
              <TableCell>
                <TextField
                  label={tCommon('Stock')}
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled
                  value={stock}
                  style={{ minWidth: '65px' }}
                />
              </TableCell>
              <TableCell>
                <NumberField
                  label={tCommon('Tax(%)')}
                  value={ShopsHelper.getAmountFormatted(shop, taxPercentage)}
                  onChange={(e) => handleChangeTaxPercentage(e.target.value)}
                  style={{ minWidth: '60px' }}
                />
              </TableCell>
              <TableCell>
                <NumberField
                  label={tCommon('Tax Amount')}
                  value={ShopsHelper.getAmountFormatted(shop, tax)}
                  onChange={(e) => handleChangeTaxAmount(e.target.value)}
                  style={{ minWidth: '100px' }}
                />
              </TableCell>
              <TableCell>
                <NumberField
                  label={tCommon('Total')}
                  value={ShopsHelper.getAmountFormatted(shop, totalAmount)}
                  onChange={(e) => handleChangePayable(e.target.value)}
                  style={{ minWidth: '100px' }}
                />
              </TableCell>
              <TableCell style={{ verticalAlign: 'bottom' }}>
                <IconButton disabled={itemPlu === '' || itemName === '' || cost === ''} onClick={addItem}>
                  <LibraryAddIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
      <TableContainer style={{ marginTop: 4 }} component={Paper}>
        <Table aria-label="customized table" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell style={{ minWidth: '50px' }} className={styles.columnCell}>
                {tCommon('PLU')}
              </TableCell>
              <TableCell style={{ minWidth: '200px' }} className={styles.nameColumn}>
                {tCommon('Item Name')}
              </TableCell>
              <TableCell style={{ minWidth: '100px' }} className={styles.columnCell}>
                {tCommon('Cost')}
              </TableCell>
              <TableCell style={{ minWidth: '100px' }} className={styles.columnCell}>
                {tCommon('Quantity')}
              </TableCell>
              <TableCell style={{ minWidth: '100px' }} className={styles.columnCell}>
                {tCommon('Stock')}
              </TableCell>
              <TableCell style={{ minWidth: '100px' }} className={styles.columnCell}>
                {tCommon('Total Tax')}
              </TableCell>
              <TableCell style={{ minWidth: '100px' }} className={styles.columnCell}>
                {tCommon('Total')}
              </TableCell>
              <TableCell className={styles.columnCell}>{tCommon('Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {returnedItems &&
              returnedItems.map((purchasedItem, idx) => (
                <TableRow key={purchasedItem.id}>
                  <TableCell>{purchasedItem.plu}</TableCell>
                  <TableCell>
                    {purchasedItem.item.name}
                    {purchasedItem.batch_data && purchasedItem.batch_data.length > 0 && (
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>
                        {purchasedItem.batch_data.map((sb, entryIdx) => {
                          const batch = (localBatches || []).find((b) => String(b.id) === String(sb.batch_id));
                          return (
                            <div key={sb.batch_id}>
                              {batch?.batch_name || sb.batch_id}: {sb.quantity}
                              {entryIdx < purchasedItem.batch_data.length - 1 ? ', ' : ''}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>{purchasedItem.rate}</TableCell>
                  <TableCell>{purchasedItem.quantity}</TableCell>
                  <TableCell>{purchasedItem.item?.stock - purchasedItem.quantity}</TableCell>
                  <TableCell>{purchasedItem.tax}</TableCell>
                  <TableCell>
                    {ShopsHelper.getAmountFormatted(
                      shop,
                      Number(purchasedItem.rate) * Number(purchasedItem.quantity) + Number(purchasedItem.tax)
                    )}
                  </TableCell>
                  <TableCell colSpan={2} style={{ display: 'flex' }}>
                    <IconButton onClick={() => dltItem(purchasedItem)}>
                      <DeleteIcon />
                    </IconButton>
                    <IconButton onClick={() => handleEditItem(purchasedItem)}>
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            <TableRow key={'total'} style={{ fontWeight: 'bold' }}>
              <TableCell></TableCell>
              <TableCell>{tCommon('Total')}</TableCell>
              <TableCell></TableCell>
              <TableCell>{summary.totalQty}</TableCell>
              <TableCell></TableCell>
              <TableCell>{summary.totalTax}</TableCell>
              <TableCell>{summary.totalPayable}</TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <div className={styles.changeable} style={{ marginTop: 12 }}>
          <h3 className={styles.subTitle} style={{ marginBottom: 16 }}>
            {tOperations('Payment Information')}
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: 8 }}>
            {(paymentModes || []).map((pm) => (
              <div key={pm.mode_id} style={{ display: 'flex', flexDirection: 'column', width: '220px' }}>
                <NumberField
                  label={pm.name}
                  value={pm.amount || ''}
                  onChange={(e) => {
                    const updatedAmount = Number(e.target.value);
                    setPaymentModes((prev) =>
                      prev.map((item) => (item.mode_id === pm.mode_id ? { ...item, amount: updatedAmount } : item))
                    );
                  }}
                />
                <Button
                  size="small"
                  variant="text"
                  color="primary"
                  className={styles.payFullBtn}
                  style={{
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    alignSelf: 'flex-end',
                    marginTop: '2px',
                    padding: 0,
                  }}
                  title={tOperations('Pay Full')}
                  onClick={() => {
                    const payableAmount = Number(summary.totalPayable);
                    setPaymentModes((prev) =>
                      prev.map((item) =>
                        item.mode_id === pm.mode_id ? { ...item, amount: payableAmount } : { ...item, amount: 0 }
                      )
                    );
                  }}
                >
                  {tOperations('Payment In Full')}
                </Button>
              </div>
            ))}
          </div>
        </div>
      <Grid container className={styles.submitSection} justifyContent="flex-end">
        <Link to={ROUTES.PURCHASE_HISTORY} className={styles.link}>
          <Button variant="contained" color="secondary" className={styles.closeBtn}>
            {tCommon('Cancel')}
          </Button>
        </Link>
        <Button
          variant="contained"
          color="primary"
          style={{ backgroundColor: '#00a65a' }}
          disabled={invoiceNumber === '' || returnDate === '' || returnedItems.length === 0 || loadingIndicator}
          onClick={handleShowReturnTypePopup}
        >
          {editData.id ? tOperations('Update Purchase Return') : tOperations('Save Purchase Return')}
        </Button>
      </Grid>
      {isShowAddVendonDialog && <NewVendorDialog onClose={closeCreateVendorDialog} onSuccess={onCreateVendor} />}
      {showReturnTypePopup && (
        <RefundTypePopup
          closeDialog={handleClosePurchseReturnTypePopup}
          handleRefundType={handleAddPurchaseReturn}
          subject={tOperations('Purchase Return')}
          hidePaymentModeSelection={true}
        />
      )}
    </div>
  );
}

export default withConsoleBase(AddPurchaseReturn);
